import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUnsavedChanges } from '../../utils/useUnsavedChanges';
import {
  Box, Container, Text, VStack, HStack, Button, Flex, Icon, IconButton,
  FormControl, FormLabel, Input, Select, Textarea, Checkbox,
  Tabs, TabList, Tab, TabPanels, TabPanel,
  SimpleGrid, Badge, Divider,
  InputGroup, InputLeftElement,
  useToast,
} from '@chakra-ui/react';
import { ArrowBackIcon, SearchIcon, CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaCheckCircle, FaCloudUploadAlt, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

/* ─── DropZone (file upload) ────────────────────────────────────────────── */
const DropZone = ({ label, files, setFiles, previews, setPreviews, fileNames, setFileNames }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.[0]) processFiles(e.dataTransfer.files); };
  const handleChange = (e) => { e.preventDefault(); if (e.target.files?.[0]) processFiles(e.target.files); };

  const processFiles = (fileList) => {
    const arr = Array.from(fileList);
    setFiles(prev => [...prev, ...arr]);
    setPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setFileNames(prev => [...prev, ...arr.map(f => f.name)]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setFileNames(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="600" color="gray.600">{label}</FormLabel>
      <Box
        onClick={() => inputRef.current.click()}
        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
        p={6} borderWidth={2} borderStyle="dashed" borderRadius="lg"
        bg={dragActive ? 'blue.50' : 'gray.50'}
        borderColor={dragActive ? 'blue.400' : 'gray.200'}
        _hover={{ borderColor: 'blue.300' }}
        cursor="pointer" textAlign="center" transition="all 0.2s"
      >
        <VStack spacing={2}>
          <Icon as={FaCloudUploadAlt} boxSize={8} color="blue.400" />
          <Text fontSize="sm" color="gray.500">{dragActive ? 'Drop files here' : 'Drag & drop or click to browse'}</Text>
          <Text fontSize="xs" color="gray.400">PDF, JPG, PNG</Text>
        </VStack>
        <input ref={inputRef} type="file" multiple accept="application/pdf,image/*" onChange={handleChange} style={{ display: 'none' }} />
      </Box>
      {previews.length > 0 && (
        <VStack spacing={1} mt={2} align="stretch">
          {previews.map((_, i) => (
            <Flex key={i} justify="space-between" align="center" p={2} bg="white" border="1px solid" borderColor="gray.100" borderRadius="md">
              <HStack><Icon as={FaCloudUploadAlt} color="green.500" boxSize={3} /><Text fontSize="xs" fontWeight="500">{fileNames[i]}</Text></HStack>
              <IconButton icon={<FaTimes />} size="xs" variant="ghost" colorScheme="red" onClick={(e) => { e.stopPropagation(); removeFile(i); }} aria-label="Remove" />
            </Flex>
          ))}
        </VStack>
      )}
    </FormControl>
  );
};

/* ─── Helpers ───────────────────────────────────────────────────────────── */
function normalizeUuid(value) {
  if (!value || value === 'undefined' || value === 'null') return null;
  return value;
}

async function generateDrawCode() {
  try {
    let allCodes = [], from = 0;
    const pageSize = 1000;
    while (true) {
      const { data } = await supabase.from('submissions').select('draw_code').not('draw_code', 'is', null).range(from, from + pageSize - 1);
      if (!data || data.length === 0) break;
      allCodes = allCodes.concat(data);
      if (data.length < pageSize) break;
      from += pageSize;
    }
    let max = 1000;
    for (const row of allCodes) { const n = parseInt(row.draw_code, 10); if (!isNaN(n) && n > max) max = n; }
    return String(max + 1);
  } catch { return String(Date.now()).slice(-8); }
}

async function insertWithDrawCodeRetry(dbData, maxRetries = 5) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (!dbData.draw_code || attempt > 0) {
      dbData.draw_code = await generateDrawCode();
      if (attempt > 0) dbData.draw_code = String(parseInt(dbData.draw_code, 10) + attempt);
    }
    const { data, error } = await supabase.from('submissions').insert([dbData]).select('id').single();
    if (!error) return { data, error: null };
    if (error.message?.includes('draw_code') && attempt < maxRetries - 1) continue;
    return { data: null, error };
  }
  return { data: null, error: { message: 'Failed to generate unique draw code after retries' } };
}

async function uploadFiles(files, type) {
  const urls = [];
  for (const file of files) {
    const ext = file.name.split('.').pop();
    const name = `${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
    const path = `${type}/${name}`;
    const { error } = await supabase.storage.from('uploads').upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: true });
    if (error) { console.error('Upload error:', error); continue; }
    const { data: { publicUrl } } = supabase.storage.from('uploads').getPublicUrl(path);
    urls.push(publicUrl);
  }
  return urls;
}

/* ─── Initial form state ────────────────────────────────────────────────── */
const initialForm = {
  patientFirstName: '', patientLastName: '', patientDOB: '', patientGender: '',
  patientEmail: '', patientStreet: '', patientCity: '', patientState: '', patientZip: '',
  insuranceId: '', policyNumber: '', groupNumber: '', subscriberId: '', relationshipToSubscriber: 'Self',
  doctorName: '', doctorEmail: '', clientName: '',
  phlebotomistId: '', bloodCollectionDate: '', bloodCollectionTime: '',
  specialInstructions: '', statTest: false,
};

/* ─── Specimen types ────────────────────────────────────────────────────── */
const SPECIMEN_TYPES = [
  'Blood - General','BLOOD SMEAR','Blue Top tube','Ear Swab','Endocervical swab','Eye Swab',
  'Fecal Smear','Fecal Swab','Fluid','Gray Top tube - Sodium Flouride','GREEN-TOP TUBE','HAIR',
  'iFOBT Stool','Lavender Top Tube','Lesion swab','Light Blue Tube','MICROGRAPH','MISC',
  'Nail Clippings','NAILS','Nasal (mid-turbinate) swab','Nasal Swab (Default)',
  'Nasopharyngeal swab','Oral Fluid Toxicology','Oral Swab','Oropharyngeal swab','Para-Pak',
  'PCR','Pink Top tube','Plain Red Top Tube','Plasma','Royal Blue Top Tube','Serum',
  'Serum Separator tube','SPUTUM','Stool,Fresh','Swab','Synovial fluid','Urea breath bags',
  'Urethral swab','Urine Cup','Urine in Gray top tube','Urine Molecular','Urine Toxicology',
  'Vaginal swab','Whole Blood','Wound','Yellow Top Tube - ACD A or B',
];

/* ─── Main component ────────────────────────────────────────────────────── */
const DoctorOrderEntry = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Session
  const [doctorSession, setDoctorSession] = useState(null);

  // Tabs
  const [activeTab, setActiveTab] = useState(0);

  // Form
  const [form, setForm] = useState(initialForm);
  const [selectedTests, setSelectedTests] = useState([]);

  // Lookups
  const [testTypes, setTestTypes] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [phlebotomists, setPhlebotomists] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedPhlebotomist, setSelectedPhlebotomist] = useState(null);

  // Search states
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [testSearch, setTestSearch] = useState('');
  const [filteredTests, setFilteredTests] = useState([]);
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [phlebSearch, setPhlebSearch] = useState('');
  const [showPhlebDropdown, setShowPhlebDropdown] = useState(false);

  // Warn user about unsaved changes
  const hasFormData = form.patientFirstName || form.patientLastName || selectedTests?.length > 0;
  useUnsavedChanges(!!hasFormData);

  // Specimen & Diagnosis
  const [specimens, setSpecimens] = useState([{ quantity: 1, type: '' }]);
  const [specimenSearches, setSpecimenSearches] = useState({});
  const [showSpecimenDropdown, setShowSpecimenDropdown] = useState(null);
  const [diagnosis, setDiagnosis] = useState(['']);
  const [previousDiagnoses, setPreviousDiagnoses] = useState([]);
  const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);

  // Refs for click-outside
  const patientRef = useRef(null);
  const testRef = useRef(null);
  const phlebRef = useRef(null);
  const specimenRef = useRef(null);
  const diagnosisRef = useRef(null);

  // File uploads
  const [scriptFiles, setScriptFiles] = useState([]);
  const [scriptPreviews, setScriptPreviews] = useState([]);
  const [scriptNames, setScriptNames] = useState([]);
  const [insuranceFiles, setInsuranceFiles] = useState([]);
  const [insurancePreviews, setInsurancePreviews] = useState([]);
  const [insuranceNames, setInsuranceNames] = useState([]);
  const [patientIdFiles, setPatientIdFiles] = useState([]);
  const [patientIdPreviews, setPatientIdPreviews] = useState([]);
  const [patientIdNames, setPatientIdNames] = useState([]);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedDrawCode, setSubmittedDrawCode] = useState('');

  /* ── Session load ───────────────────────────────────────────────────── */
  useEffect(() => {
    const raw = localStorage.getItem('doctorSession');
    if (!raw) { navigate('/doctor/login'); return; }
    try {
      const s = JSON.parse(raw);
      setDoctorSession(s);
      setForm(prev => ({ ...prev, doctorName: s.full_name || '', doctorEmail: s.email || '', clientName: s.clinic_name || '' }));
    } catch { localStorage.removeItem('doctorSession'); navigate('/doctor/login'); }
  }, [navigate]);

  /* ── Paginated fetch helper ─────────────────────────────────────────── */
  const fetchAllRows = useCallback(async (table, selectQuery, orderCol = 'name') => {
    const PAGE = 1000;
    let allData = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table).select(selectQuery).order(orderCol).range(from, from + PAGE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    return allData;
  }, []);

  /* ── Data fetching ──────────────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    if (!doctorSession) return;
    const selectCols = 'id, name, code, description, cash_price, alternative_name';
    const [testsData, panelsData, insRes, phlebRes, patientsRes] = await Promise.all([
      fetchAllRows('tests', selectCols),
      fetchAllRows('panels', selectCols),
      supabase.from('insurances').select('*').eq('is_active', true).order('name'),
      supabase.from('phlebotomist_profiles').select('id, full_name, company_name, email, phone, company_address, archived').order('company_name'),
      supabase.from('submissions')
        .select('patient_name, patient_email, patient_dob, patient_gender, patient_street, patient_city, patient_state, patient_zip')
        .eq('doctor_email', doctorSession.email).order('patient_name').limit(500),
    ]);

    const allTests = [
      ...testsData.map(t => ({ ...t, type: 'test' })),
      ...panelsData.map(p => ({ ...p, type: 'panel' })),
    ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setTestTypes(allTests);

    setInsurances(insRes.data || []);
    setPhlebotomists((phlebRes.data || []).filter(p => !p.archived));

    // Fetch previous diagnoses
    try {
      const { data: diagData } = await supabase.from('submissions').select('diagnosis').not('diagnosis', 'is', null).not('diagnosis', 'eq', '').limit(500);
      const unique = [...new Set((diagData || []).map(d => d.diagnosis).filter(Boolean))].sort();
      setPreviousDiagnoses(unique);
    } catch (e) { console.error('Error fetching previous diagnoses:', e); }

    // Deduplicate patients by name
    const seen = new Set();
    const unique = (patientsRes.data || []).filter(p => {
      if (!p.patient_name || seen.has(p.patient_name)) return false;
      seen.add(p.patient_name);
      return true;
    });
    setPatients(unique);
  }, [doctorSession]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── Acronym matching (e.g. "BMP" → "Basic Metabolic Panel") ──────── */
  const matchesAcronym = useCallback((name, search) => {
    if (!name || !search) return false;
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length < 2) return false;
    const acronym = words.map(w => w[0]).join('').toLowerCase();
    return acronym === search || acronym.startsWith(search);
  }, []);

  /* ── Test filtering ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!testSearch.trim()) { setFilteredTests(testTypes.slice(0, 20)); return; }
    const q = testSearch.toLowerCase();
    setFilteredTests(testTypes.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.alternative_name || '').toLowerCase().includes(q) ||
      matchesAcronym(t.name, q)
    ).slice(0, 30));
  }, [testSearch, testTypes, matchesAcronym]);

  /* ── Click-outside ──────────────────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (patientRef.current && !patientRef.current.contains(e.target)) setShowPatientDropdown(false);
      if (testRef.current && !testRef.current.contains(e.target)) setShowTestDropdown(false);
      if (phlebRef.current && !phlebRef.current.contains(e.target)) setShowPhlebDropdown(false);
      if (specimenRef.current && !specimenRef.current.contains(e.target)) setShowSpecimenDropdown(null);
      if (diagnosisRef.current && !diagnosisRef.current.contains(e.target)) setShowDiagnosisDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Form helpers ───────────────────────────────────────────────────── */
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const selectPatient = (p) => {
    const name = (p.patient_name || '').trim();
    let lastName = '', firstName = '';
    if (name.includes(',')) {
      // "Last, First" format
      const parts = name.split(',').map(s => s.trim());
      lastName = parts[0];
      firstName = parts.slice(1).join(' ');
    } else {
      // "First Last" or "First Middle Last" format
      const words = name.split(/\s+/);
      if (words.length === 1) {
        lastName = words[0];
      } else {
        lastName = words[words.length - 1];
        firstName = words.slice(0, -1).join(' ');
      }
    }
    setForm(prev => ({
      ...prev,
      patientLastName: lastName,
      patientFirstName: firstName,
      patientDOB: p.patient_dob || '',
      patientGender: p.patient_gender || '',
      patientEmail: p.patient_email || '',
      patientStreet: p.patient_street || '',
      patientCity: p.patient_city || '',
      patientState: p.patient_state || '',
      patientZip: p.patient_zip || '',
    }));
    setPatientSearch(p.patient_name || '');
    setShowPatientDropdown(false);
  };

  const selectTest = (test) => {
    if (selectedTests.some(t => t.id === test.id)) return;
    setSelectedTests(prev => [...prev, test]);
    setTestSearch('');
    setShowTestDropdown(false);
  };

  const removeTest = (id) => setSelectedTests(prev => prev.filter(t => t.id !== id));

  const selectPhlebotomist = (p) => {
    setSelectedPhlebotomist(p);
    updateForm('phlebotomistId', p.id);
    setPhlebSearch(p.company_name || p.full_name);
    setShowPhlebDropdown(false);
  };

  const filteredPatients = patients.filter(p =>
    !patientSearch.trim() || (p.patient_name || '').toLowerCase().includes(patientSearch.toLowerCase())
  ).slice(0, 15);

  const filteredPhlebs = phlebotomists.filter(p => {
    if (!phlebSearch.trim()) return true;
    const q = phlebSearch.toLowerCase();
    return (p.company_name || '').toLowerCase().includes(q) || (p.full_name || '').toLowerCase().includes(q);
  }).slice(0, 15);

  /* ── Submit ─────────────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!form.patientLastName.trim() || !form.patientFirstName.trim()) {
      toast({ title: 'Patient name is required', status: 'warning', duration: 3000 }); setActiveTab(0); return;
    }
    if (selectedTests.length === 0) {
      toast({ title: 'Select at least one test', status: 'warning', duration: 3000 }); setActiveTab(1); return;
    }

    setSubmitting(true);
    try {
      // Upload files
      const [scriptUrls, insuranceUrls, patientIdUrls] = await Promise.all([
        scriptFiles.length > 0 ? uploadFiles(scriptFiles, 'script') : [],
        insuranceFiles.length > 0 ? uploadFiles(insuranceFiles, 'insurance') : [],
        patientIdFiles.length > 0 ? uploadFiles(patientIdFiles, 'patientId') : [],
      ]);

      const patientName = [form.patientLastName, form.patientFirstName].filter(Boolean).join(', ');
      const patientAddress = [form.patientStreet, form.patientCity, form.patientState, form.patientZip].filter(Boolean).join(', ');
      const selectedIns = insurances.find(i => i.id === form.insuranceId);

      const dbData = {
        patient_name: patientName,
        patient_address: patientAddress,
        patient_street: form.patientStreet || '',
        patient_city: form.patientCity || '',
        patient_state: form.patientState || '',
        patient_zip: form.patientZip || '',
        patient_email: form.patientEmail || '',
        patient_dob: form.patientDOB || null,
        patient_gender: form.patientGender || '',
        doctor_name: doctorSession.full_name || '',
        doctor_email: doctorSession.email || '',
        client_name: form.clientName || doctorSession.clinic_name || null,
        tests_on_the_script: selectedTests.map(t => ({ id: t.id })),
        blood_collection_time: form.bloodCollectionDate && form.bloodCollectionTime
          ? `${form.bloodCollectionDate}T${form.bloodCollectionTime}` : form.bloodCollectionDate || '',
        script_image: scriptUrls,
        insurance_card_image: insuranceUrls,
        patient_id_image: patientIdUrls,
        insurance_id: normalizeUuid(form.insuranceId),
        insurance_company: selectedIns?.name || '',
        insurance_policy_number: form.policyNumber || '',
        policy_number: form.policyNumber || '',
        group_number: form.groupNumber || '',
        subscriber_id: form.subscriberId || '',
        relationship_to_subscriber: form.relationshipToSubscriber || 'Self',
        need_fedex_label: false,
        stat_test: form.statTest || false,
        special_instructions: form.specialInstructions || '',
        ...(specimens.some(s => s.type) ? { specimen_type: JSON.stringify(specimens.filter(s => s.type)) } : {}),
        ...(diagnosis.some(d => d.trim()) ? { diagnosis: diagnosis.filter(d => d.trim()).join('; ') } : {}),
        phlebotomist_name: selectedPhlebotomist?.company_name || selectedPhlebotomist?.full_name || '',
        phlebotomist_email: selectedPhlebotomist?.email || '',
        phlebotomist_id: normalizeUuid(form.phlebotomistId),
        created_by_user: `DR-${doctorSession.full_name || 'Doctor'}`,
        status: 'pending',
        submitted_at: new Date().toISOString(),
      };

      dbData.draw_code = await generateDrawCode();
      const { error } = await insertWithDrawCodeRetry(dbData);
      if (error) throw error;

      setSubmittedDrawCode(dbData.draw_code);
      setSubmitted(true);
      toast({ title: 'Order submitted!', description: `Accession #${dbData.draw_code}`, status: 'success', duration: 5000 });
    } catch (err) {
      toast({ title: 'Submission failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Reset for new order ────────────────────────────────────────────── */
  const resetForm = () => {
    setForm({ ...initialForm, doctorName: doctorSession?.full_name || '', doctorEmail: doctorSession?.email || '', clientName: doctorSession?.clinic_name || '' });
    setSelectedTests([]); setSelectedPhlebotomist(null);
    setSpecimens([{ quantity: 1, type: '' }]); setSpecimenSearches({}); setShowSpecimenDropdown(null);
    setDiagnosis(['']); setShowDiagnosisDropdown(false);
    setScriptFiles([]); setScriptPreviews([]); setScriptNames([]);
    setInsuranceFiles([]); setInsurancePreviews([]); setInsuranceNames([]);
    setPatientIdFiles([]); setPatientIdPreviews([]); setPatientIdNames([]);
    setPatientSearch(''); setTestSearch(''); setPhlebSearch('');
    setActiveTab(0); setSubmitted(false); setSubmittedDrawCode('');
  };

  /* ── Success screen ─────────────────────────────────────────────────── */
  if (submitted) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Box bg="white" borderBottom="1px solid" borderColor="gray.100" position="sticky" top={0} zIndex={10}>
          <Container maxW="container.xl" py={3}>
            <Flex justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="700" color="gray.800">Order Submitted</Text>
              <Text fontSize="sm" color="gray.400">Dr. {doctorSession?.full_name}</Text>
            </Flex>
          </Container>
        </Box>
        <Container maxW="container.sm" py={16}>
          <VStack spacing={6} textAlign="center">
            <Icon as={FaCheckCircle} boxSize={16} color="green.400" />
            <Text fontSize="2xl" fontWeight="700" color="gray.800">Order Submitted Successfully</Text>
            <Badge bg="blue.50" color="blue.600" fontSize="lg" fontFamily="mono" px={4} py={2} borderRadius="md">
              Accession #{submittedDrawCode}
            </Badge>
            <Text color="gray.500">Your order has been submitted and is pending processing by the phlebotomist.</Text>
            <HStack spacing={3} pt={4}>
              <Button colorScheme="blue" onClick={() => navigate('/doctor/dashboard')}>Back to Dashboard</Button>
              <Button variant="outline" onClick={resetForm}>Create Another Order</Button>
            </HStack>
          </VStack>
        </Container>
      </Box>
    );
  }

  /* ── Dropdown component ─────────────────────────────────────────────── */
  const SearchDropdown = ({ items, renderItem, maxH = "200px" }) => (
    <Box position="absolute" top="100%" left={0} right={0} zIndex={20} bg="white" border="1px solid" borderColor="gray.200"
      borderRadius="md" boxShadow="lg" maxH={maxH} overflowY="auto" mt={1}>
      {items.length === 0 ? (
        <Text p={3} fontSize="sm" color="gray.400" textAlign="center">No results found</Text>
      ) : items.map(renderItem)}
    </Box>
  );

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={3}>
          <Flex justify="space-between" align="center">
            <HStack spacing={3}>
              <IconButton icon={<ArrowBackIcon />} variant="ghost" size="sm" onClick={() => navigate('/doctor/dashboard')} aria-label="Back" />
              <Text fontSize="lg" fontWeight="700" color="gray.800">New Order</Text>
            </HStack>
            <Text fontSize="sm" color="gray.400">Dr. {doctorSession?.full_name}</Text>
          </Flex>
        </Container>
      </Box>

      <Container maxW="container.lg" py={6}>
        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue" isLazy>
          <TabList mb={6}>
            <Tab fontWeight="600" fontSize="sm">#1 Patient / Insurance</Tab>
            <Tab fontWeight="600" fontSize="sm">#2 Order Information</Tab>
            <Tab fontWeight="600" fontSize="sm">#3 Documents & Submit</Tab>
          </TabList>

          <TabPanels>
            {/* ── TAB 1: Patient & Insurance ─────────────────────────────── */}
            <TabPanel p={0}>
              <VStack spacing={6} align="stretch">
                {/* Patient Section */}
                <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={5}>
                  <Box display="inline-block" bg="blue.50" px={2} py={0.5} borderRadius="md" mb={4}>
                    <Text fontSize="xs" fontWeight="600" color="blue.600">Patient Information</Text>
                  </Box>

                  {/* Patient search */}
                  <Box ref={patientRef} position="relative" mb={4}>
                    <FormLabel fontSize="sm" color="gray.600">Search Existing Patient</FormLabel>
                    <InputGroup size="sm">
                      <InputLeftElement><SearchIcon color="gray.300" boxSize={3} /></InputLeftElement>
                      <Input
                        placeholder="Type patient name to search..."
                        value={patientSearch}
                        onChange={(e) => { setPatientSearch(e.target.value); setShowPatientDropdown(true); }}
                        onFocus={() => setShowPatientDropdown(true)}
                      />
                    </InputGroup>
                    {showPatientDropdown && filteredPatients.length > 0 && (
                      <SearchDropdown
                        items={filteredPatients}
                        renderItem={(p, i) => (
                          <Box key={i} px={3} py={2} cursor="pointer" _hover={{ bg: 'blue.50' }} onClick={() => selectPatient(p)}>
                            <Text fontSize="sm" fontWeight="500">{p.patient_name}</Text>
                            {p.patient_email && <Text fontSize="xs" color="gray.400">{p.patient_email}</Text>}
                          </Box>
                        )}
                      />
                    )}
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" color="gray.600">Last Name</FormLabel>
                      <Input size="sm" value={form.patientLastName} onChange={e => updateForm('patientLastName', e.target.value)} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" color="gray.600">First Name</FormLabel>
                      <Input size="sm" value={form.patientFirstName} onChange={e => updateForm('patientFirstName', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Date of Birth</FormLabel>
                      <Input size="sm" type="date" value={form.patientDOB} onChange={e => updateForm('patientDOB', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={4}>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Gender</FormLabel>
                      <Select size="sm" value={form.patientGender} onChange={e => updateForm('patientGender', e.target.value)} placeholder="Select...">
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Email</FormLabel>
                      <Input size="sm" type="email" value={form.patientEmail} onChange={e => updateForm('patientEmail', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Client / Facility</FormLabel>
                      <Input size="sm" value={form.clientName} onChange={e => updateForm('clientName', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mt={4}>
                    <FormControl gridColumn="span 2">
                      <FormLabel fontSize="sm" color="gray.600">Street Address</FormLabel>
                      <Input size="sm" value={form.patientStreet} onChange={e => updateForm('patientStreet', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">City</FormLabel>
                      <Input size="sm" value={form.patientCity} onChange={e => updateForm('patientCity', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">State</FormLabel>
                      <Input size="sm" value={form.patientState} onChange={e => updateForm('patientState', e.target.value)} placeholder="e.g. FL" />
                    </FormControl>
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mt={4}>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Zip Code</FormLabel>
                      <Input size="sm" value={form.patientZip} onChange={e => updateForm('patientZip', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Insurance Section */}
                <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={5}>
                  <Box display="inline-block" bg="green.50" px={2} py={0.5} borderRadius="md" mb={4}>
                    <Text fontSize="xs" fontWeight="600" color="green.600">Insurance Information</Text>
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Insurance Provider</FormLabel>
                      <Select size="sm" value={form.insuranceId} onChange={e => updateForm('insuranceId', e.target.value)} placeholder="Select insurance...">
                        {insurances.map(ins => (
                          <option key={ins.id} value={ins.id}>{ins.name} {ins.type ? `(${ins.type})` : ''}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Policy Number</FormLabel>
                      <Input size="sm" value={form.policyNumber} onChange={e => updateForm('policyNumber', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Group Number</FormLabel>
                      <Input size="sm" value={form.groupNumber} onChange={e => updateForm('groupNumber', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Member / Subscriber ID</FormLabel>
                      <Input size="sm" value={form.subscriberId} onChange={e => updateForm('subscriberId', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Relationship to Subscriber</FormLabel>
                      <Select size="sm" value={form.relationshipToSubscriber} onChange={e => updateForm('relationshipToSubscriber', e.target.value)}>
                        <option value="Self">Self</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Child">Child</option>
                        <option value="Other">Other</option>
                      </Select>
                    </FormControl>
                  </SimpleGrid>
                </Box>

                <Flex justify="flex-end">
                  <Button colorScheme="blue" size="sm" onClick={() => setActiveTab(1)}>Next: Order Information</Button>
                </Flex>
              </VStack>
            </TabPanel>

            {/* ── TAB 2: Order Information ────────────────────────────────── */}
            <TabPanel p={0}>
              <VStack spacing={6} align="stretch">
                <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={5}>
                  <Box display="inline-block" bg="purple.50" px={2} py={0.5} borderRadius="md" mb={4}>
                    <Text fontSize="xs" fontWeight="600" color="purple.600">Order Information</Text>
                  </Box>

                  {/* Phlebotomist selection */}
                  <Box ref={phlebRef} position="relative" mb={4}>
                    <FormLabel fontSize="sm" color="gray.600">Phlebotomist / Phlebotomist</FormLabel>
                    <InputGroup size="sm">
                      <InputLeftElement><SearchIcon color="gray.300" boxSize={3} /></InputLeftElement>
                      <Input
                        placeholder="Search phlebotomist..."
                        value={phlebSearch}
                        onChange={(e) => { setPhlebSearch(e.target.value); setShowPhlebDropdown(true); }}
                        onFocus={() => setShowPhlebDropdown(true)}
                      />
                    </InputGroup>
                    {selectedPhlebotomist && (
                      <Badge mt={1} colorScheme="purple" variant="subtle" fontSize="xs">
                        {selectedPhlebotomist.company_name || selectedPhlebotomist.full_name}
                        <IconButton icon={<CloseIcon />} size="2xs" variant="ghost" ml={1} onClick={() => { setSelectedPhlebotomist(null); updateForm('phlebotomistId', ''); setPhlebSearch(''); }} aria-label="Clear" />
                      </Badge>
                    )}
                    {showPhlebDropdown && (
                      <SearchDropdown
                        items={filteredPhlebs}
                        renderItem={(p) => (
                          <Box key={p.id} px={3} py={2} cursor="pointer" _hover={{ bg: 'purple.50' }} onClick={() => selectPhlebotomist(p)}>
                            <Text fontSize="sm" fontWeight="500">{p.company_name || p.full_name}</Text>
                            <Text fontSize="xs" color="gray.400">{p.email} {p.company_address ? `· ${p.company_address}` : ''}</Text>
                          </Box>
                        )}
                      />
                    )}
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Collection Date</FormLabel>
                      <Input size="sm" type="date" value={form.bloodCollectionDate} onChange={e => updateForm('bloodCollectionDate', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">Collection Time</FormLabel>
                      <Input size="sm" type="time" value={form.bloodCollectionTime} onChange={e => updateForm('bloodCollectionTime', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <Divider my={4} />

                  {/* Test selection */}
                  <Box ref={testRef} position="relative" mb={4}>
                    <FormLabel fontSize="sm" color="gray.600">Tests <Text as="span" color="red.400">*</Text></FormLabel>
                    {selectedTests.length > 0 && (
                      <Flex flexWrap="wrap" gap={1} mb={2}>
                        {selectedTests.map(t => (
                          <Badge key={t.id} colorScheme={t.type === 'panel' ? 'purple' : 'teal'} variant="subtle" px={2} py={0.5} borderRadius="full" fontSize="xs">
                            {t.name} {t.code ? `(${t.code})` : ''}
                            <IconButton icon={<CloseIcon />} size="2xs" variant="ghost" ml={1} onClick={() => removeTest(t.id)} aria-label="Remove" />
                          </Badge>
                        ))}
                      </Flex>
                    )}
                    <InputGroup size="sm">
                      <InputLeftElement><SearchIcon color="gray.300" boxSize={3} /></InputLeftElement>
                      <Input
                        placeholder="Search tests by name or code..."
                        value={testSearch}
                        onChange={(e) => { setTestSearch(e.target.value); setShowTestDropdown(true); }}
                        onFocus={() => setShowTestDropdown(true)}
                      />
                    </InputGroup>
                    {showTestDropdown && (
                      <SearchDropdown
                        items={filteredTests.filter(t => !selectedTests.some(s => s.id === t.id))}
                        maxH="250px"
                        renderItem={(t) => (
                          <Box key={t.id} px={3} py={2} cursor="pointer" _hover={{ bg: 'teal.50' }} onClick={() => selectTest(t)}>
                            <HStack justify="space-between">
                              <Text fontSize="sm" fontWeight="500">{t.name}</Text>
                              <Badge fontSize="2xs" colorScheme={t.type === 'panel' ? 'purple' : 'gray'}>{t.type === 'panel' ? 'Panel' : 'Test'}</Badge>
                            </HStack>
                            <Text fontSize="xs" color="gray.400">{[t.code, t.description].filter(Boolean).join(' · ')}</Text>
                          </Box>
                        )}
                      />
                    )}
                  </Box>

                  <Divider my={4} />

                  {/* Specimen */}
                  <Box ref={specimenRef} mb={4}>
                    <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>Specimen</Text>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="md">
                      <HStack bg="gray.100" px={3} py={2} spacing={4}>
                        <Text fontSize="xs" fontWeight="bold" w="70px">Quantity</Text>
                        <Text fontSize="xs" fontWeight="bold" flex={1}>Specimen</Text>
                        <Box w="28px" />
                      </HStack>
                      {specimens.map((row, idx) => {
                        const searchVal = specimenSearches[idx] || '';
                        const filtered = searchVal.trim()
                          ? SPECIMEN_TYPES.filter(s => s.toLowerCase().includes(searchVal.toLowerCase()))
                          : SPECIMEN_TYPES;
                        return (
                          <HStack key={idx} px={3} py={2} spacing={4} borderTop="1px solid" borderColor="gray.100" align="flex-start">
                            <Select size="sm" w="70px" value={row.quantity}
                              onChange={(e) => {
                                const updated = [...specimens];
                                updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value) };
                                setSpecimens(updated);
                              }}
                            >
                              {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                            </Select>
                            <Box flex={1} position="relative">
                              {row.type ? (
                                <HStack justify="space-between" bg="gray.50" px={2} py={1} borderRadius="md">
                                  <Text fontSize="sm">{row.type}</Text>
                                  <Text fontSize="xs" cursor="pointer" color="gray.400" _hover={{ color: 'red.500' }}
                                    onClick={() => {
                                      const updated = [...specimens];
                                      updated[idx] = { ...updated[idx], type: '' };
                                      setSpecimens(updated);
                                    }}
                                  >&times;</Text>
                                </HStack>
                              ) : (
                                <>
                                  <Input size="sm" placeholder="Search specimen..."
                                    value={searchVal}
                                    onChange={(e) => {
                                      setSpecimenSearches(prev => ({ ...prev, [idx]: e.target.value }));
                                      setShowSpecimenDropdown(idx);
                                    }}
                                    onFocus={() => setShowSpecimenDropdown(idx)}
                                  />
                                  {showSpecimenDropdown === idx && (
                                    <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" boxShadow="lg" maxH="200px" overflowY="auto" zIndex={30} mt={1}>
                                      {filtered.length === 0 && (
                                        <Text px={3} py={2} fontSize="sm" color="gray.500" textAlign="center">No matches</Text>
                                      )}
                                      {filtered.map(s => (
                                        <Box key={s} px={3} py={1.5} cursor="pointer" fontSize="sm" _hover={{ bg: 'blue.50' }}
                                          borderBottom="1px solid" borderColor="gray.50"
                                          onClick={() => {
                                            const updated = [...specimens];
                                            updated[idx] = { ...updated[idx], type: s };
                                            setSpecimens(updated);
                                            setShowSpecimenDropdown(null);
                                            setSpecimenSearches(prev => ({ ...prev, [idx]: '' }));
                                          }}
                                        >
                                          {s}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </>
                              )}
                            </Box>
                            <IconButton icon={<CloseIcon />} size="xs" variant="ghost" colorScheme="red" aria-label="Remove row"
                              isDisabled={specimens.length <= 1}
                              onClick={() => setSpecimens(prev => prev.filter((_, i) => i !== idx))}
                            />
                          </HStack>
                        );
                      })}
                    </Box>
                    <Button size="sm" variant="ghost" colorScheme="blue" mt={2}
                      onClick={() => setSpecimens(prev => [...prev, { quantity: 1, type: '' }])}
                    >
                      + Add Row
                    </Button>
                  </Box>

                  <Divider my={4} />

                  {/* Diagnosis */}
                  <Box ref={diagnosisRef} mb={4}>
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontSize="sm" fontWeight="600" color="gray.700">Diagnosis</Text>
                      {previousDiagnoses.length > 0 && (
                        <Box position="relative">
                          <Button size="sm" colorScheme="blue" onClick={() => setShowDiagnosisDropdown(!showDiagnosisDropdown)}>
                            Previous Diagnosis
                          </Button>
                          {showDiagnosisDropdown && (
                            <Box position="absolute" top="100%" right={0} bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" boxShadow="lg" maxH="200px" overflowY="auto" zIndex={30} mt={1} minW="250px">
                              {previousDiagnoses.map((d) => (
                                <Box key={d} px={3} py={2} cursor="pointer" _hover={{ bg: 'blue.50' }} borderBottom="1px solid" borderColor="gray.100"
                                  onClick={() => {
                                    const emptyIdx = diagnosis.findIndex(v => !v.trim());
                                    const updated = [...diagnosis];
                                    if (emptyIdx >= 0) {
                                      updated[emptyIdx] = d;
                                    } else {
                                      updated.push(d);
                                    }
                                    setDiagnosis(updated);
                                    setShowDiagnosisDropdown(false);
                                  }}
                                >
                                  <Text fontSize="sm">{d}</Text>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Flex>
                    {diagnosis.map((diag, idx) => (
                      <HStack key={idx} mb={2}>
                        <Input
                          size="sm"
                          value={diag}
                          onChange={(e) => {
                            const updated = [...diagnosis];
                            updated[idx] = e.target.value;
                            setDiagnosis(updated);
                          }}
                          placeholder="Enter diagnosis or ICD code..."
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Remove diagnosis"
                          isDisabled={diagnosis.length <= 1}
                          onClick={() => setDiagnosis(prev => prev.filter((_, i) => i !== idx))}
                        />
                      </HStack>
                    ))}
                    <Button size="sm" variant="link" colorScheme="blue"
                      onClick={() => setDiagnosis(prev => [...prev, ''])}
                    >
                      + Add Row
                    </Button>
                  </Box>

                  <Divider my={4} />

                  <FormControl mb={4}>
                    <FormLabel fontSize="sm" color="gray.600">Special Instructions</FormLabel>
                    <Textarea size="sm" rows={3} value={form.specialInstructions} onChange={e => updateForm('specialInstructions', e.target.value)} placeholder="Any special instructions for the phlebotomist..." />
                  </FormControl>

                </Box>

                <Flex justify="space-between">
                  <Button variant="outline" size="sm" onClick={() => setActiveTab(0)}>Back</Button>
                  <Button colorScheme="blue" size="sm" onClick={() => setActiveTab(2)}>Next: Documents & Submit</Button>
                </Flex>
              </VStack>
            </TabPanel>

            {/* ── TAB 3: Documents & Submit ───────────────────────────────── */}
            <TabPanel p={0}>
              <VStack spacing={6} align="stretch">
                {/* File Uploads */}
                <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={5}>
                  <Box display="inline-block" bg="orange.50" px={2} py={0.5} borderRadius="md" mb={4}>
                    <Text fontSize="xs" fontWeight="600" color="orange.600">Documents</Text>
                  </Box>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <DropZone label="Doctor's Script / Prescription" files={scriptFiles} setFiles={setScriptFiles} previews={scriptPreviews} setPreviews={setScriptPreviews} fileNames={scriptNames} setFileNames={setScriptNames} />
                    <DropZone label="Patient ID" files={patientIdFiles} setFiles={setPatientIdFiles} previews={patientIdPreviews} setPreviews={setPatientIdPreviews} fileNames={patientIdNames} setFileNames={setPatientIdNames} />
                    <DropZone label="Insurance Card" files={insuranceFiles} setFiles={setInsuranceFiles} previews={insurancePreviews} setPreviews={setInsurancePreviews} fileNames={insuranceNames} setFileNames={setInsuranceNames} />
                  </SimpleGrid>
                </Box>

                {/* Review Summary */}
                <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={5}>
                  <Box display="inline-block" bg="blue.50" px={2} py={0.5} borderRadius="md" mb={4}>
                    <Text fontSize="xs" fontWeight="600" color="blue.600">Review Summary</Text>
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Patient */}
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" mb={2}>Patient</Text>
                      <VStack align="stretch" spacing={1}>
                        <Text fontSize="sm"><strong>Name:</strong> {[form.patientLastName, form.patientFirstName].filter(Boolean).join(', ') || '—'}</Text>
                        <Text fontSize="sm"><strong>DOB:</strong> {form.patientDOB || '—'}</Text>
                        <Text fontSize="sm"><strong>Gender:</strong> {form.patientGender || '—'}</Text>
                        <Text fontSize="sm"><strong>Email:</strong> {form.patientEmail || '—'}</Text>
                        <Text fontSize="sm"><strong>Address:</strong> {[form.patientStreet, form.patientCity, form.patientState, form.patientZip].filter(Boolean).join(', ') || '—'}</Text>
                      </VStack>
                    </Box>

                    {/* Insurance */}
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" mb={2}>Insurance</Text>
                      <VStack align="stretch" spacing={1}>
                        <Text fontSize="sm"><strong>Provider:</strong> {insurances.find(i => i.id === form.insuranceId)?.name || '—'}</Text>
                        <Text fontSize="sm"><strong>Policy #:</strong> {form.policyNumber || '—'}</Text>
                        <Text fontSize="sm"><strong>Group #:</strong> {form.groupNumber || '—'}</Text>
                        <Text fontSize="sm"><strong>Member ID:</strong> {form.subscriberId || '—'}</Text>
                      </VStack>
                    </Box>

                    {/* Order */}
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" mb={2}>Order Details</Text>
                      <VStack align="stretch" spacing={1}>
                        <Text fontSize="sm"><strong>Phlebotomist:</strong> {selectedPhlebotomist?.company_name || selectedPhlebotomist?.full_name || '—'}</Text>
                        <Text fontSize="sm"><strong>Collection:</strong> {[form.bloodCollectionDate, form.bloodCollectionTime].filter(Boolean).join(' ') || '—'}</Text>
                        <Text fontSize="sm"><strong>Tests:</strong> {selectedTests.length > 0 ? selectedTests.map(t => t.name).join(', ') : '—'}</Text>
                        {specimens.some(s => s.type) && (
                          <Box>
                            <Text fontSize="sm" fontWeight="bold">Specimen:</Text>
                            {specimens.filter(s => s.type).map((s, i) => <Text key={i} fontSize="sm" ml={2}>{s.quantity}x {s.type}</Text>)}
                          </Box>
                        )}
                        {diagnosis.some(d => d.trim()) && (
                          <Box>
                            <Text fontSize="sm" fontWeight="bold">Diagnosis:</Text>
                            {diagnosis.filter(d => d.trim()).map((d, i) => <Text key={i} fontSize="sm" ml={2}>{d}</Text>)}
                          </Box>
                        )}
                        {form.specialInstructions && <Text fontSize="sm"><strong>Instructions:</strong> {form.specialInstructions}</Text>}
                      </VStack>
                    </Box>

                    {/* Doctor */}
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" mb={2}>Doctor</Text>
                      <VStack align="stretch" spacing={1}>
                        <Text fontSize="sm"><strong>Name:</strong> Dr. {form.doctorName}</Text>
                        <Text fontSize="sm"><strong>Email:</strong> {form.doctorEmail}</Text>
                        <Text fontSize="sm"><strong>Client:</strong> {form.clientName || '—'}</Text>
                      </VStack>
                    </Box>
                  </SimpleGrid>

                  {/* Documents count */}
                  <Divider my={4} />
                  <HStack spacing={4}>
                    <Text fontSize="xs" color="gray.500">Script: {scriptFiles.length} file(s)</Text>
                    <Text fontSize="xs" color="gray.500">Patient ID: {patientIdFiles.length} file(s)</Text>
                    <Text fontSize="xs" color="gray.500">Insurance Card: {insuranceFiles.length} file(s)</Text>
                  </HStack>
                </Box>

                <Flex justify="space-between">
                  <Button variant="outline" size="sm" onClick={() => setActiveTab(1)}>Back</Button>
                  <Button
                    colorScheme="blue"
                    size="md"
                    px={8}
                    onClick={handleSubmit}
                    isLoading={submitting}
                    loadingText="Submitting..."
                  >
                    Submit Order
                  </Button>
                </Flex>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
    </Box>
  );
};

export default DoctorOrderEntry;
